import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import Card from '@mui/material/Card'
import Slide from '@mui/material/Slide'
import Box from '@mui/material/Box/Box'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import MultiTenantDrawerSkeleton from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerSkeleton'
import Typography from '@mui/material/Typography'
import useData from '@user/features/MapView/MultiTenantDrawer/hooks/useData'
import {
  EntityLocationDTO,
  MultiTenantLocationDetails,
  salesCenterContactLocationIds,
} from '@user/store/map/typings'
import MultiTenantDrawerContentPestPac from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerContentPestPac/MultiTenantDrawerContentPestPac'
import { useAppDispatch } from '@sales-center/common'
import { fetchMultiTenantEntityDetail } from '@user/store/map'
import MultiTenantDrawerContentSingle from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerContentSingle/MultiTenantDrawerContentSingle'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import ArrowBack from '@mui/icons-material/ArrowBack'
import MapLoadingProgress from '@user/features/MapView/MapLoadingProgress/MapLoadingProgress'

type Props = {
  show: boolean
  onClose: () => void
  setDrawerOpen: (open: boolean) => void
  drawerVariant?: 'full' | 'modal'
  onBackClick?: () => void
  refreshTrigger?: number
  onRefresh?: () => void
  setShowHistory: (show: boolean) => void
  onViewKnockStatusHistory?: (location: any) => void
  onReviewPendingLead?: (draftId: string) => void
  onViewPestPacDetails?: (location: EntityLocationDTO) => void
}

function MultiTenantDrawer(props: Props): JSX.Element {
  const {
    onClose,
    show,
    drawerVariant = 'full',
    setDrawerOpen,
    onBackClick,
    refreshTrigger,
    onRefresh,
    setShowHistory,
    onViewKnockStatusHistory,
    onReviewPendingLead,
    onViewPestPacDetails,
  } = props
  const dispatch = useAppDispatch()
  const { loading, selectedServiceLocations, locations, pestPacMembers } =
    useData()
  const isInitialMount = useRef(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (
      refreshTrigger !== undefined &&
      show &&
      selectedServiceLocations.length > 0
    ) {
      dispatch(
        fetchMultiTenantEntityDetail(
          salesCenterContactLocationIds(selectedServiceLocations),
        ),
      )
    }
  }, [refreshTrigger])

  const isFullVariant = React.useMemo(
    () => drawerVariant === 'full',
    [drawerVariant],
  )

  const isMobile = useMediaQuery('(max-width: 600px)')

  const { left, top } = useMemo(() => {
    const toolbarElement = document.getElementById('mapViewToolbarContainer')
    const toolbarWidth = toolbarElement ? toolbarElement.clientWidth : 0

    if (isFullVariant) {
      return {
        left: isMobile ? 0 : toolbarWidth,
        top: isMobile ? '64px' : 0,
      }
    }

    return {
      left: toolbarWidth + 24,
      top: 96,
    }
  }, [isFullVariant, isMobile, show])

  const noViewPermissionForAllLocations = useMemo(() => {
    return (
      locations.length > 0 &&
      pestPacMembers.length === 0 &&
      locations.filter((location) => location.hasViewPermission).length === 0
    )
  }, [locations, pestPacMembers])

  return (
    <>
      <Slide direction="right" in={show} mountOnEnter unmountOnExit>
        <Card
          sx={{
            height: isFullVariant ? `100%` : 'fit-content',
            width: isMobile ? '100vw' : 400,
            position: 'fixed',
            top,
            left,
            zIndex: 1300,
            boxShadow: 4,
            borderRadius: isFullVariant ? undefined : 3,
            overflowY: 'auto',
            paddingBottom: isMobile ? 10 : 2,
          }}
        >
          <Box
            sx={{
              paddingLeft: onBackClick ? 2 : 2.4,
              paddingRight: 2,
              pb: 0.6,
              paddingTop: 1,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1,
            }}
          >
            {onBackClick ? (
              <IconButton
                aria-label="back"
                onClick={onBackClick}
                sx={{
                  flex: 0,
                  width: 36,
                  height: 36,
                  color: '#1565C0',
                  mr: 1,
                }}
              >
                <ArrowBack />
              </IconButton>
            ) : null}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography variant="subtitle4">
                {selectedServiceLocations.length} records found for this
                location.
              </Typography>

              <Button
                variant="text"
                aria-label="close"
                onClick={onClose}
                sx={{ color: '#1565C0', minWidth: 0, width: 'fit-content' }}
                size="small"
              >
                Close
              </Button>
            </Box>
          </Box>

          {noViewPermissionForAllLocations ? (
            <Alert severity="warning" sx={{ m: 2 }}>
              You don&apos;t have permission to view these records.
            </Alert>
          ) : null}

          {loading ? (
            <MultiTenantDrawerSkeleton />
          ) : (
            <>
              {locations.map((location: MultiTenantLocationDetails) => (
                <MultiTenantDrawerContentSingle
                  key={`${location.entityType}-${location.entityId}`}
                  location={location}
                  setDrawerOpen={setDrawerOpen}
                  noViewPermissionForAllLocations={
                    noViewPermissionForAllLocations
                  }
                  setIsLoading={setIsLoading}
                  onRefresh={onRefresh}
                  setShowHistory={setShowHistory}
                  onViewKnockStatusHistory={onViewKnockStatusHistory}
                  onReviewPendingLead={onReviewPendingLead}
                />
              ))}
              {pestPacMembers.map((member) => (
                <MultiTenantDrawerContentPestPac
                  key={`pestpac-${member.pestPacLocationId}`}
                  member={member}
                  onViewFullDetails={onViewPestPacDetails}
                  onViewKnockStatusHistory={onViewKnockStatusHistory}
                  onRefresh={onRefresh}
                />
              ))}
            </>
          )}
        </Card>
      </Slide>

      {isLoading && (
        <MapLoadingProgress isRendering={Boolean(isLoading)} elevation={0} />
      )}
    </>
  )
}

export default MultiTenantDrawer
